/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useItemVariant} from '../item-variant'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Heading} from '@salesforce/retail-react-app/app/components/shared/ui'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'
import {getVariationGroupOfVariant} from '../../utils/product-utils'

/**
 * In the context of a cart product item variant, this components simply renders
 * the item's name using the cart item field `productName`. We use this field
 * rather than variant `name` field as the variant detail may not yet be
 * available upon rendering.
 */
const ItemName = ({isSmall = false, ...props}) => {
    const variant = useItemVariant()
    const productId = variant?.id
    const {triggerViewItemEvent} = useGTM()
    const variationGroup = getVariationGroupOfVariant(variant)

    /**
     * Handles the click event on a link by triggering a view item event.
     *
     * This function is responsible for managing the click event on a link element.
     * When invoked, it triggers a 'select_item' event for the specified variant.
     * This is typically used to track user interactions with item variants in the
     * application, allowing for analytics and user behavior tracking.
     *
     * @function handleLinkClick
     * @returns {void} This function does not return a value.
     */
    const handleLinkClick = () => {
        triggerViewItemEvent('select_item', variant)
    }

    return (
        <Heading as="h2">
            <Link
                {...props}
                fontSize={isSmall ? '2xl' : '3xl'}
                fontFamily="heading"
                textDecoration="none"
                textTransform="uppercase"
                to={`/product/${variationGroup?.productId || productId}`}
                onClick={handleLinkClick}
            >
                {variant?.productName || variant?.name}
            </Link>
        </Heading>
    )
}

ItemName.propTypes = {
    isSmall: PropTypes.bool
}

export default ItemName
